import React from 'react';
import { intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

// Two related codes the membership branch may produce:
// - line-item/membership-rental-{tier}-{poolRateCents}  (older $0 tracking — kept for backward compat)
// - line-item/membership-host-offset-{tier}-{poolRateCents}  (provider-side -100% offset)
const MEMBER_LINE_RE =
  /^line-item\/(?:membership-rental|membership-host-offset)-(regular|lastMinute)-(\d+)$/;

const isMembershipRentalCode = code =>
  typeof code === 'string' && MEMBER_LINE_RE.test(code);

const parseMemberLine = code => {
  const m = code?.match(MEMBER_LINE_RE);
  if (!m) return null;
  return { tier: m[1], poolRateCents: parseInt(m[2], 10) };
};

const numberFromMaybe = v => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toNumber === 'function') return v.toNumber();
  return Number(v) || 0;
};

const findBookingDays = lineItems => {
  // Order line is the unit-based line item with includeFor including provider
  // (typically line-item/day or line-item/night). Read its quantity to get
  // the booking length.
  const orderLine = lineItems.find(
    li =>
      typeof li.code === 'string' &&
      (li.code === 'line-item/day' || li.code === 'line-item/night') &&
      !li.reversal
  );
  if (!orderLine) return 0;
  return numberFromMaybe(orderLine.quantity || orderLine.units);
};

/**
 * Provider-side: renders ONLY the "Pool payout (paid by WetRentals)" line.
 * The Sharetribe offset accounting line is intentionally not shown to the
 * host — they only need to see what they'll actually receive. The offset
 * line is still in the transaction for Sharetribe payout math and ops
 * reconciliation.
 *
 * Customer-side: doesn't render (offset is provider-only). The member's
 * credit shows via LineItemMemberDiscountMaybe instead.
 */
const LineItemMembershipRentalMaybe = props => {
  const { lineItems, intl, isProvider } = props;
  if (!isProvider) return null;

  const memberLine = lineItems.find(
    item =>
      isMembershipRentalCode(item.code) &&
      !item.reversal &&
      (Array.isArray(item.includeFor) ? item.includeFor.includes('provider') : true)
  );
  if (!memberLine) return null;

  const parsed = parseMemberLine(memberLine.code);
  const tier = parsed?.tier;
  const poolRateCents = parsed?.poolRateCents || 0;
  const tierLabel = tier === 'lastMinute' ? 'Last-Minute Membership' : 'Summer Membership';
  const days = findBookingDays(lineItems);
  if (!days || !poolRateCents) return null;

  const currency = memberLine.unitPrice?.currency || 'CAD';
  const expectedPayoutCents = poolRateCents * days;
  // Did the booking get cancelled / declined / refunded? Sharetribe appends
  // a matching reversal line item when a refund fires. When that's present,
  // we still show the pool payout for context, then a refund line below
  // that nets it to $0 — host should see the full story, not a phantom payout.
  const isRefunded = lineItems.some(
    item => item.code === memberLine.code && item.reversal
  );

  return (
    <>
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          Pool payout · {tierLabel} · {days} day{days === 1 ? '' : 's'} ×{' '}
          {formatMoney(intl, new Money(poolRateCents, currency))}/day
        </span>
        <span className={css.itemValue}>
          {formatMoney(intl, new Money(expectedPayoutCents, currency))}
        </span>
      </div>
      {isRefunded ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            Pool payout refunded (booking cancelled)
          </span>
          <span className={css.itemValue}>
            −{formatMoney(intl, new Money(expectedPayoutCents, currency))}
          </span>
        </div>
      ) : null}
    </>
  );
};

LineItemMembershipRentalMaybe.propTypes = {
  intl: intlShape,
};

LineItemMembershipRentalMaybe.isMembershipRentalCode = isMembershipRentalCode;

export default LineItemMembershipRentalMaybe;
